import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

import { SpinnerService } from '../../services/spinner.service';
import { ApiQuestSet, ApiSetQuestSetRequest, ApiQuestion, ApiFacility, ApiFile, SettingFile } from '../../api-client-module';

import { AppConst } from '../../app-const';
import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { UploadedFile } from '../../firestore-models/uploaded-file';
import { TranslateService } from '@ngx-translate/core';

import { MultiStringMessageDialogComponent } from '../../common/multi-string-message-dialog/multi-string-message-dialog.component';
import { SelectQuestionsDialogComponent } from '../select-questions-dialog/select-questions-dialog.component';
import { SelectUploadedFileDialogComponent } from '../../select-uploaded-file-dialog/select-uploaded-file-dialog.component';

export interface QuestionData {
  change_type: number;
  quest_set?: ApiQuestSet;
  facility?: ApiFacility;
}

@Component({
  selector: 'app-quest-set-dialog',
  templateUrl: './quest-set-dialog.component.html',
  styleUrls: ['./quest-set-dialog.component.css'],
  standalone: false,
})
export class QuestSetDialogComponent implements OnInit {

  quest_set?: ApiQuestSet;

  // 施設情報
  facility?: ApiFacility;
  questionIDs: string[] = [];
  projectId: string;

  dlFiles: SettingFile[] = [];

  isAdded: boolean = false;

  questionTypes = AppConst.QUESTION_ITEM_TYPES;
  projectLanguages!: Array<ProjectLanguage>;

  formGroup!: FormGroup;
  idControl!: FormControl;
  nameControl!: FormControl;
  questionTitlesControl!: FormControl;
  dispOrderControl!: FormControl;
  useQuestionControl!: FormControl;

  currentLang: string = 'ja';
  projectBaseLang: string = 'ja';

  constructor(
    private angularFirestore: AngularFirestore,
    public dialogRef: MatDialogRef<QuestSetDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: QuestionData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
  ) {
    this.projectId = this.configurationService.getProjectId();
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.currentLang = this.translate.currentLang;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.quest_set = this.data.quest_set;
    if (!this.quest_set) {
      this.quest_set = {
        id: '',
        name: '',
        question_titles: [],
        disp_order: 1,
        use_question: false,
        questions: [],
        project_id: this.projectId,
        dl_files: [],
        enabled: false
      }
      this.isAdded = true;
    }
    if (!this.quest_set.dl_files) {
      this.quest_set['dl_files'] = [];
    }
    this.getQuestionIDs();
    this.createForm();
    this.idControl = this.formGroup.get('id') as FormControl;
    this.nameControl = this.formGroup.get('name') as FormControl;
    this.questionTitlesControl = this.formGroup.get('questionTitles') as FormControl;
    this.dispOrderControl = this.formGroup.get('dispOrder') as FormControl;
    this.useQuestionControl = this.formGroup.get('useQuestion') as FormControl;

    this.projectLanguages = this.configurationService.getProject().languages!;
    this.getFiles();
  }

  public getErrorMessageToQuestionTitle() {
    return this.questionTitlesControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }
  public getErrorMessageToDispOrder() {
    return this.dispOrderControl.hasError('required')
    ? this.translate.instant('必須項目')
    : this.dispOrderControl.hasError('pattern') || this.dispOrderControl.hasError('min')
    ? this.translate.instant('数字（1以上）')
    : '';
  }

  private createForm() {
    this.formGroup = this.formBuilder.group(
      {
        id: [this.quest_set!.id, []],
        name: [this.quest_set!.name, []],
        questionTitles: [this.quest_set!.question_titles, [Validators.required]],
        dispOrder: [this.quest_set!.disp_order, [Validators.required, Validators.min(1), Validators.pattern('^[0-9]+$')]],
        useQuestion: [this.quest_set!.use_question, []],
      }
    );
  }

  ngAfterViewInit(): void {
  }

  // アンケートタイトルダイアログ
  openQuestionTitleDialog(): void {
    const dialogRef = this.dialog.open(MultiStringMessageDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { title: this.translate.instant('アンケートタイトル'), string_messages: this.quest_set!.question_titles }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.quest_set!.question_titles = result;
        this.questionTitlesControl.setValue(this.quest_set!.question_titles);
      }
    });
  }

  // 設問選択ダイアログ
  openSelectQuestionsDialog(): void {
    const dialogRef = this.dialog.open(SelectQuestionsDialogComponent, {
      width: '80%',
      disableClose: true,
      data: { floor: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        if (!this.quest_set!.questions) {
          this.quest_set!.questions = new Array<ApiQuestion>();
        }
        var questionIds = [];
        for ( const question of this.quest_set!.questions ) {
          if (question) {
            questionIds.push(question.question_id);
          }
        }
        let isChanged: boolean = false;
        for (const row of result) {
          if (!questionIds.includes(row.question_id)) {
            this.quest_set!.questions.push(row);
            isChanged = true;
          }
        }
        if (isChanged && this.quest_set) {
          this.getQuestionIDs();
        }
      }
    });
  }

  // 施設に紐付く設問情報設定
  private getQuestionIDs() {
    this.questionIDs = [];
    if (!!this.quest_set! && !!this.quest_set!.questions && this.quest_set!.questions.length > 0) {
      for (const question of this.quest_set?.questions) {
        this.questionIDs.push(question.question_id);
      }
    }
  }
  
  // 設問情報並べ替え
  onDropQuestion(event: CdkDragDrop<string[]>) {
    let questions = [];
    if (this.quest_set && this.quest_set.questions && this.quest_set.questions.length > 1) {
      for (const question of this.quest_set.questions) {
        questions.push({...question});
      }
      moveItemInArray(this.questionIDs, event.previousIndex, event.currentIndex);
      let questionLists: Array<ApiQuestion> = [];
      for (const question_id of this.questionIDs) {
        for (let question of questions) {
          if (question_id === question.question_id) {
            questionLists.push({...question});
          }
        }
      }
      this.quest_set!.questions = questionLists;
    }
  }

  // 設問リストの削除
  deleteQuestion(question_id: string): void {
    if (!!this.quest_set!.questions!) {
      this.quest_set!.questions.forEach((question, index) => {
        if (question.question_id == question_id) {
          this.quest_set!.questions.splice(index, 1);
        }
      })
    }
  }

  addQuestSet(): void {
    if (this.formGroup.valid) {
      this.quest_set!.name = this.nameControl.value;
      this.quest_set!.question_titles = this.questionTitlesControl.value;
      this.quest_set!.disp_order = this.dispOrderControl.value;
      this.quest_set!.use_question = this.useQuestionControl.value;
      if (this.quest_set) {
        let request: ApiSetQuestSetRequest = {
          access_token: '',
          change_type: this.data.change_type,
          quest_set: [this.quest_set],
          project_id: this.projectId,
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setQuestSet(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  // 取消ボタン
  onNoClick(): void {
    this.dialogRef.close();
  }

  // 画像ファイル設定
  private getFiles() {
    this.dlFiles = [];
    if (!!this.quest_set!.dl_files && this.quest_set!.dl_files.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトのアップロードファイルを全て取得してから
      //       施設情報に登録されている id で絞り込む。
      this.angularFirestore.collection<UploadedFile>(
        AppConst.FIRESTORE_UPLOADED_FILES, ref => ref.where('project_id', '==', this.projectId).where('type', '==', 'DL')
        ).valueChanges({ idField: 'id' }).subscribe(docs => {
          this.dlFiles = [];
          if (!!docs) {
            for (const dlFile of this.quest_set!.dl_files!) {
              for (const uploadedFile of docs) {
                if (dlFile.file_id === uploadedFile.id) {
                  const settingFile: SettingFile = {
                    id: uploadedFile.id,
                    file_name: uploadedFile.file_name,
                    download_url: uploadedFile.download_url,
                    lang_code: dlFile.lang_code,
                  }
                  this.dlFiles.push(settingFile);
                  break;
                }
              }
            }
          }
      });
    }
  }

  // 画像ファイル選択ダイアログ
  openSelectFilesDialog(): void {
    const dialogRef = this.dialog.open(SelectUploadedFileDialogComponent, {
      width: '90%',
      disableClose: true,
      data: { uploadedFile: null, type: 'DL' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        for (const row of result) {
          const lang = this.projectLanguages[0].lang_code;
          this.quest_set!.dl_files!.push({file_id: row.id, lang_code: lang});
        }
        this.getFiles();
      }
    });
  }

  // 画像情報並べ替え
  onDropFile(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.dlFiles, event.previousIndex, event.currentIndex);
    let files: Array<ApiFile> = [];
    for (const file of this.dlFiles) {
      files.push({file_id: file.id, lang_code: file.lang_code});
    }
    this.quest_set!.dl_files = files;
  }

  // 変更された言語を登録
  changeDLFileLang(i: number, event: any) {
    this.quest_set!.dl_files![i].lang_code = event.value;
    this.dlFiles![i].lang_code = event.value;
  }

  deleteFiles(i: number): void {
    this.quest_set!.dl_files!.splice(i, 1);
    this.getFiles();
  }

}
